import type { ExecutionDashboardSlice, ExecutionProgressHydration } from '../../executionOperations/types';
import { buildEffectiveExecutionView } from './routeExecutionProjectionFacade';

export type ExecutionProgressView = {
  routePercent: number;
  loadingPercent: number;
  unloadingPercent: number;
  cargoPercent: number;
  stopsCompleted: number;
  stopsTotal: number;
  cargoDeliveredQty: number;
  cargoPlannedQty: number;
  loadingLoadedQty: number;
  loadingPlannedQty: number;
  unloadingDeliveredQty: number;
  unloadingPlannedQty: number;
  partialCargoCount: number;
  failedCargoCount: number;
};

export function computeExecutionProgressView(params: {
  hydration: ExecutionProgressHydration | null | undefined;
  dashboard?: ExecutionDashboardSlice | null;
}): ExecutionProgressView | null {
  const { hydration, dashboard } = params;
  if (!hydration) return null;

  const effective = buildEffectiveExecutionView(hydration);
  if (!effective) return null;

  const progress = effective.progress;
  const cargoRows = effective.cargo;
  const stopRows = effective.stops;

  const loadingPlannedQty = cargoRows.reduce((s, c) => s + c.plannedQty, 0);
  const loadingLoadedQty = cargoRows.reduce((s, c) => s + c.loadedQty, 0);
  const unloadingDeliveredQty = cargoRows.reduce((s, c) => s + c.unloadedQty, 0);
  const cargoDeliveredQty = cargoRows.filter((c) => c.isDelivered).length;

  const loadingPercent =
    loadingPlannedQty > 0
      ? Math.round((loadingLoadedQty / loadingPlannedQty) * 100)
      : progress.loadingPercent;
  const unloadingPercent =
    loadingPlannedQty > 0
      ? Math.round((unloadingDeliveredQty / loadingPlannedQty) * 100)
      : progress.unloadingPercent;

  const stopsCompleted = stopRows.filter((s) => s.isCompleted).length;
  const stopsTotal = stopRows.length;
  const routePercent =
    stopsTotal > 0
      ? Math.round((stopsCompleted / stopsTotal) * 100)
      : dashboard?.executionProgressPercent ?? progress.overallPercent;

  return {
    routePercent,
    loadingPercent: dashboard?.loadingProgressPercent ?? loadingPercent,
    unloadingPercent: dashboard?.unloadingProgressPercent ?? unloadingPercent,
    cargoPercent: dashboard?.cargoProgressPercent ?? progress.cargoPercent,
    stopsCompleted: dashboard?.stopsSummary.completed ?? stopsCompleted,
    stopsTotal: dashboard?.stopsSummary.total ?? stopsTotal,
    cargoDeliveredQty: dashboard?.cargoSummary.delivered ?? cargoDeliveredQty,
    cargoPlannedQty: dashboard?.cargoSummary.total ?? hydration.cargo.length,
    loadingLoadedQty,
    loadingPlannedQty,
    unloadingDeliveredQty,
    unloadingPlannedQty: loadingPlannedQty,
    partialCargoCount: cargoRows.filter((c) => c.isPartiallyDelivered).length,
    failedCargoCount: cargoRows.filter((c) => c.executionState === 'FAILED').length
  };
}
